import {useForm} from "@mantine/form";
import {Box, Button, Container, Paper, PasswordInput, rem, TextInput} from "@mantine/core";
import {useContext} from "react";
import AuthContext from "../../contexts/auth/AuthContext";
import Notifications from "../../utils/notifications/Notifications";
import {useNavigate} from "react-router-dom";
import classes from "./Login.module.css";
import useFetching from "../../hooks/useFetching";
import AnimateIn from "../../utils/animations/AnimateIn";

/**
 * Login page.
 * @constructor
 * @author Kacper Faber
 * @since 0.1.0
 */
function Login() {
    /**
     * Auth context
     * @author Kacper Faber
     * @since 0.1.0
     */
    const authContext = useContext(AuthContext);

    /**
     * Is actually loading data from API.
     * If set, loader on login button will be active.
     * @author Kacper Faber
     * @since 0.1.0
     */
    const [isLoading, startLoading, stopLoading] = useFetching(false);

    /**
     * Navigate function to navigate between routes
     * @author Kacper Faber
     * @since 0.1.0
     */
    const nav = useNavigate();

    /**
     * Simple login form controller.
     * @author Kacper Faber
     * @since 0.1.0
     */
    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            username: '',
            password: ''
        }
    });

    const onSubmitSuccess = () => {
        Notifications.onLoginSuccess();
        nav("/");
    }

    const onSubmitFailed = () => {
        Notifications.onLoginFailed();
    }

    /**
     * On form submit.
     * Actually it's using
     * @param values
     */
    const onSubmit = (values: any) => {
        const {username, password} = values;

        startLoading();

        authContext.login(username, password)
            .then(onSubmitSuccess)
            .catch(onSubmitFailed)
            .finally(stopLoading);
    }

    return (
        <Box bg="primary" className={classes.root}>
            <Container size={420} pt={rem(100)} className={classes.container}>
                <AnimateIn transition="pop" duration={500}>
                    <Paper radius="md" p={30} className={classes.paper}>
                        <form onSubmit={form.onSubmit(onSubmit)}>
                            <TextInput
                                radius={0}
                                placeholder="Nazwa użytkownika"
                                size="md"
                                key={form.key('username')}
                                {...form.getInputProps('username')}
                            />

                            <PasswordInput
                                radius={0}
                                placeholder="Hasło"
                                mt="md"
                                size="md"
                                key={form.key('password')}
                                {...form.getInputProps('password')}
                            />

                            <Button
                                radius={0}
                                fullWidth
                                fz="sm"
                                mt="md"
                                size="md"
                                type="submit"
                                children="Zaloguj się"
                                loading={isLoading}
                            />
                        </form>
                    </Paper>
                </AnimateIn>
            </Container>
        </Box>
    );
}

export default Login;